import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/constants/apiRoutes';
import { queryKeys } from 'src/constants/querykeys';
import axiosInstance from 'src/utils/axios';

interface DepartmentRequest {
  companyId?: string;
  departmentId: number;
  name: string;
  glCodes: number[];
  members: number[];
}

interface UseUpdateDepartmentMutationOptions {
  onSuccess?: () => void;
  onError?: (error: any) => void;
}

const useUpdateDepartmentMutation = ({
  onSuccess,
  onError,
}: UseUpdateDepartmentMutationOptions = {}) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (params: DepartmentRequest) => {
      const { companyId, departmentId, ...rest } = params;
      const res = await axiosInstance.put<DepartmentRequest[]>(
        api.updateDepartment(departmentId, companyId),
        rest
      );
      return res.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate department list queries to refetch updated data. The clients query is
      // intentionally NOT invalidated here: assigning/unassigning members on a department
      // doesn't change any client record itself (ClientResponse has no department fields),
      // and refetching it in parallel with the department list causes the two queries to
      // land at different times. That race made the member assignment/unassignment effect
      // run once against a stale department snapshot (briefly showing the member in the
      // wrong list) before correcting itself once the department refetch caught up.
      if (variables.companyId) {
        queryClient.invalidateQueries({
          queryKey: [...queryKeys.department.departmentList(variables.companyId.toString())],
        });
      }
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });

  return mutation;
};

export default useUpdateDepartmentMutation;
